import React, { useMemo, useState } from 'react';
import { Modal, Button, Select, SelectOption } from 'components';
import { getMultipleTeams } from 'api/TeamController';
import { ResultType } from 'api/TeamController/getMultipleTeams';
import styled from 'styled-components';

type AddToTeamContactsModalProps = {
  visible: boolean;
  onCancel: () => void;
  onDontAdd: () => void;
  onAdd: (selectedTeams?: string[]) => void;
  teamAccess?: string[];
};

const AddToTeamContactsModal = ({
  visible,
  onCancel,
  onDontAdd,
  onAdd,
  teamAccess,
}: AddToTeamContactsModalProps) => {
  const hasMultipleTeamsAccess = useMemo(
    () => teamAccess && teamAccess?.length > 1,
    [teamAccess],
  );
  const [addButtonDisabled, setAddButtonDisabled] = useState(false);
  const [selectedTeams, setSelectedTeams] = useState<string[] | []>([]);
  const [multipleTeams, setMultipleTeams] = useState<ResultType | []>([]);
  const handleAddClick = () => {
    setAddButtonDisabled(true);
    if (hasMultipleTeamsAccess) {
      onAdd(selectedTeams);
    } else {
      onAdd();
    }
  };

  React.useEffect(() => {
    if (visible) setAddButtonDisabled(false);
    if (hasMultipleTeamsAccess) {
      const fetchData = async () => {
        const teams = await getMultipleTeams(teamAccess!);
        setMultipleTeams(teams);
        setSelectedTeams(teams.map(team => team.id));
      };
      fetchData();
    }
  }, [hasMultipleTeamsAccess, teamAccess, visible]);

  return (
    <Modal
      open={visible}
      title={'Add Team Admin to Listing Contacts?'}
      closable={false}
      footer={[
        <Button key="cancel" data-cy="cancel-button" onClick={onCancel}>
          Cancel
        </Button>,
        <Button
          key="dontadd"
          data-cy="dontadd-button"
          type="primary"
          danger
          onClick={onDontAdd}
        >
          Don&apos;t Add
        </Button>,
        <Button
          key="add"
          data-cy="add-button"
          type="primary"
          disabled={
            addButtonDisabled ||
            (hasMultipleTeamsAccess && !selectedTeams.length)
          }
          onClick={handleAddClick}
        >
          Add
        </Button>,
      ]}
    >
      {`Do you want to add this user as a contact to all listings for ${
        hasMultipleTeamsAccess ? 'the following teams' : 'this team'
      }?`}

      {hasMultipleTeamsAccess && multipleTeams && (
        <StyledSelect
          data-cy="select-teams"
          mode="multiple"
          placeholder="Select a team"
          value={selectedTeams}
          onChange={val => setSelectedTeams(val)}
          error={!selectedTeams.length && 'At least one team must be selected'}
          position="after"
          filterOption={(input, option) =>
            (option.children as unknown as string)
              .toLowerCase()
              .indexOf(input.toLowerCase()) >= 0
          }
        >
          {multipleTeams.map((team: { id: string; name: string }) => (
            <SelectOption value={team.id} key={team.id}>
              {team.name}
            </SelectOption>
          ))}
        </StyledSelect>
      )}
    </Modal>
  );
};

export default AddToTeamContactsModal;

const StyledSelect = styled(Select)`
  margin-top: 12px;
  & span.ant-typography-danger {
    margin-top: 8px;
    font-size: 14px;
  }
`;
